import React, { useState } from 'react';

const JOBS = [
  {
    company:  'Devstudio',
    location: 'Lahore, Pakistan',
    role:     'Senior Full Stack Software Engineer',
    period:   'Mar 2024 – Present',
    current:  true,
    project: {
      name:     'Mailboxing',
      subtitle: 'Express Logistics & Shipment Management',
      url:      'https://www.mailboxes.bm',
    },
    points: [
      'Refactored large-scale Laravel legacy systems, cutting API response times from 10–20 seconds to ~200ms and significantly improving platform reliability.',
      'Led full-stack version upgrades (Angular v8 to v16, Laravel v8 to v10), introducing a scalable modular architecture.',
      'Integrated ChatGPT and Gemini APIs to automate invoice processing and drive intelligent workflow automation.',
      'Engineered real-time shipment and inventory tracking systems using Laravel Reverb and Socket.io.',
      'Implemented chunk-based exports and queue systems to reliably process 50k–100k shipment records.',
      'Integrated Bermuda Customs, WhatsApp, Klaviyo, Mailgun, and payment gateways into enterprise workflows.',
      'Designed operational dashboards and analytics systems to enhance monitoring and customer experience.',
    ],
    tech: ['Laravel', 'Angular', 'Laravel Reverb', 'Socket.io', 'ChatGPT', 'Gemini', 'MySQL', 'AWS'],
  },
  {
    company:  'Coding Pixel',
    location: 'Lahore, Pakistan',
    role:     'Senior Full Stack Software Engineer',
    period:   'Nov 2021 – Mar 2024',
    current:  false,
    projects: [
      {
        name:     'Fyfer',
        subtitle: 'Fitness & Social Platform',
        url:      'https://fyferapp.com',
        points: [
          'Built scalable APIs and database systems for trainers, trainees, subscriptions, and scheduling.',
          'Integrated Stripe recurring payments, Twilio video calls, Socket.io chat, and OneSignal push notifications.',
          'Developed a Vue.js admin dashboard and optimised core user-interaction workflows.',
        ],
      },
      {
        name:     'Open Border',
        subtitle: 'Immigration Services Platform',
        url:      'https://openborders.io',
        points: [
          'Developed REST APIs and a React.js admin panel for appointments, subscriptions, and consultation management.',
          'Integrated Acuity Scheduling, Stripe payments, and Zapier automation systems.',
        ],
      },
      {
        name:     'AI Travel Guide',
        subtitle: 'Intelligent Travel Assistant Platform',
        url:      null,
        points: [
          'Built an AI-powered travel assistant using ChatGPT and Gemini for intelligent travel guidance.',
          'Integrated Google Maps APIs, route mapping, weather systems, and AI-driven recommendations.',
        ],
      },
    ],
    tech: ['Laravel', 'React.js', 'Vue.js', 'Stripe', 'Twilio', 'Socket.io', 'ChatGPT', 'Google Maps API', 'MySQL'],
  },
];

const ExternalIcon = () => (
  <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

export default function Experience() {
  const [active, setActive] = useState(0);
  const job = JOBS[active];

  return (
    <section id="experience" className="py-16 lg:py-24 bg-slate-950">
      <div className="max-w-6xl mx-auto px-5 sm:px-6">

        <p className="font-mono text-sky-400 text-xs sm:text-sm tracking-[0.15em] mb-3">02. Experience</p>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-100 mb-8 lg:mb-14 leading-tight">
          Where I've worked
        </h2>

        <div className="flex flex-col md:flex-row gap-0">

          {/* Tab list */}
          <div className="flex md:flex-col gap-0 border-b md:border-b-0 md:border-l-2 border-slate-800 mb-8 md:mb-0 overflow-x-auto md:overflow-visible shrink-0 md:min-w-[160px]">
            {JOBS.map((j, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`px-5 py-3.5 text-sm font-medium whitespace-nowrap text-left transition-all border-b-2 md:border-b-0 md:border-l-2 -mb-[2px] md:-ml-[2px] md:mb-0 ${
                  active === i
                    ? 'text-sky-400 border-sky-400 bg-sky-400/5'
                    : 'text-slate-500 border-transparent hover:text-slate-300 hover:bg-slate-800/40'
                }`}
              >
                {j.company}
              </button>
            ))}
          </div>

          {/* Panel */}
          <div className="md:pl-12 flex-1 min-w-0">

            {/* Role + period */}
            <div className="mb-6">
              <h3 className="text-xl font-bold text-slate-100 leading-snug mb-1">{job.role}</h3>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <span className="text-sky-400 font-semibold text-sm">{job.company}</span>
                <span className="text-slate-700 text-xs">·</span>
                <span className="text-slate-500 text-sm">{job.location}</span>
                <span className="font-mono text-xs text-slate-500 bg-slate-800/80 border border-slate-700 px-2.5 py-0.5 rounded-full">
                  {job.period}
                </span>
                {job.current && (
                  <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-2.5 py-0.5 rounded-full">
                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                    Current
                  </span>
                )}
              </div>
            </div>

            {/* Single project (Devstudio) */}
            {job.project && (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-mono text-[10px] text-sky-400 uppercase tracking-[0.18em]">Project</span>
                  <div className="flex-1 h-px bg-slate-800" />
                </div>
                <div className="mb-5">
                  {job.project.url ? (
                    <a
                      href={job.project.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 group"
                    >
                      <span className="text-slate-100 font-bold text-base group-hover:text-sky-400 transition-colors">
                        {job.project.name}
                      </span>
                      <span className="text-slate-500 text-sm font-normal">{job.project.subtitle}</span>
                      <ExternalIcon />
                    </a>
                  ) : (
                    <div className="flex items-baseline gap-2">
                      <span className="text-slate-100 font-bold text-base">{job.project.name}</span>
                      <span className="text-slate-500 text-sm">{job.project.subtitle}</span>
                    </div>
                  )}
                </div>
                <ul className="space-y-3 mb-6">
                  {job.points.map((pt, i) => (
                    <li key={i} className="flex gap-3 text-slate-400 text-sm leading-relaxed">
                      <span className="w-1.5 h-1.5 bg-sky-500 rounded-full shrink-0 mt-[7px]" />
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Multiple projects (Coding Pixel) */}
            {job.projects && (
              <div className="space-y-7">
                {job.projects.map((proj, pi) => (
                  <div key={pi}>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="font-mono text-[10px] text-sky-400 uppercase tracking-[0.18em]">
                        Project {pi + 1}
                      </span>
                      <div className="flex-1 h-px bg-slate-800" />
                    </div>
                    <div className="mb-3">
                      {proj.url ? (
                        <a
                          href={proj.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 group"
                        >
                          <span className="text-slate-100 font-semibold text-sm group-hover:text-sky-400 transition-colors">
                            {proj.name}
                          </span>
                          <span className="text-slate-500 text-sm font-normal">{proj.subtitle}</span>
                          <ExternalIcon />
                        </a>
                      ) : (
                        <div className="flex flex-wrap items-baseline gap-2">
                          <span className="text-slate-100 font-semibold text-sm">{proj.name}</span>
                          <span className="text-slate-500 text-sm">{proj.subtitle}</span>
                        </div>
                      )}
                    </div>
                    <ul className="space-y-2.5">
                      {proj.points.map((pt, i) => (
                        <li key={i} className="flex gap-3 text-slate-400 text-sm leading-relaxed">
                          <span className="w-1.5 h-1.5 bg-sky-500 rounded-full shrink-0 mt-[7px]" />
                          <span>{pt}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {/* Tech stack */}
            <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-slate-800">
              {job.tech.map(t => (
                <span key={t} className="font-mono text-xs text-sky-300 bg-sky-400/8 border border-sky-400/20 px-2.5 py-1 rounded-lg">
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
